import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SEO } from "@/components/SEO";
import { SectionReveal } from "@/components/shared/SectionReveal";
import apiClient from "@/lib/api";

export default function BlogArticle() {
  const { t } = useTranslation();
  const { lang = "fr", slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    apiClient
      .get(`/blog/${slug}?lang=${lang}`)
      .then((res) => setArticle(res.data))
      .catch(() => setNotFound(true));
  }, [slug, lang]);

  if (notFound) {
    return (
      <div className="py-32 text-center" data-testid="blog-article-not-found">
        <p className="text-slate-500 mb-4">{t("blog.empty")}</p>
        <Link to={`/${lang}/blog`} className="text-deepblue font-semibold">{t("blog.back_to_blog")}</Link>
      </div>
    );
  }

  if (!article) return <div className="py-32" data-testid="blog-article-loading" />;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.created_at,
    author: { "@type": "Organization", name: "Fastority" },
  };

  return (
    <div data-testid="blog-article-page">
      <SEO title={article.meta_title || article.title} description={article.meta_description || article.excerpt} path={`blog/${slug}`} schema={schema} />

      <section className="bg-deepblue text-white py-24 sm:py-28 grain-overlay relative overflow-hidden">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 relative">
          <Link to={`/${lang}/blog`} data-testid="blog-article-back" className="inline-flex items-center gap-2 text-white/60 text-sm mb-6 hover:text-white transition-colors">
            <ArrowLeft size={15} /> {t("blog.back_to_blog")}
          </Link>
          <span className="text-xs uppercase tracking-wide font-bold text-coral">{t(`blog.categories.${article.category}`, article.category)}</span>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-3" data-testid="blog-article-title">
            {article.title}
          </h1>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-offwhite">
        <SectionReveal className="max-w-2xl mx-auto px-5 sm:px-8">
          <div
            data-testid="blog-article-content"
            className="prose prose-slate max-w-none text-slate-700 leading-relaxed [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-2xl [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-slate900 [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-xl [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </SectionReveal>
      </section>
    </div>
  );
}
